import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { authAPI } from '../../api/axios';
import { toast } from 'react-toastify';
import { Loader2, ArrowLeft, ShieldCheck } from 'lucide-react';

// Usage:
//   Staff:   /forgot-password  (redirects to /login after reset)
//   Company: /forgot-password?type=company  (redirects to /company/login)

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isCompany = searchParams.get('type') === 'company';

  const [step, setStep] = useState(1); // 1 = enter phone, 2 = OTP + new password
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [devOtp, setDevOtp] = useState('');

  const loginPath = isCompany ? '/company/login' : '/login';
  const accentColor = isCompany ? 'blue' : 'teal';

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!phone || phone.length < 10) {
      toast.error('Enter a valid 10-digit phone number');
      return;
    }
    setLoading(true);
    try {
      const response = await authAPI.sendPasswordResetOtp(phone);
      const msg = response.data.data?.message || '';
      // Extract dev mode OTP from message
      const match = msg.match(/OTP: (\d{6})/);
      if (match) setDevOtp(match[1]);
      toast.success('OTP sent to your phone');
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) { toast.error('Enter the 6-digit OTP'); return; }
    if (newPassword.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await authAPI.resetPassword(phone, otp, newPassword);
      toast.success('Password reset successful! Please login.');
      navigate(loginPath);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = `w-full border border-gray-200 rounded-xl px-4 py-3 text-sm bg-gray-50
    focus:outline-none focus:ring-2 ${isCompany ? 'focus:ring-blue-500' : 'focus:ring-teal-500'}
    focus:border-transparent placeholder-gray-300 transition`;

  const btnClass = `w-full py-3.5 rounded-xl font-bold text-sm text-white
    flex items-center justify-center gap-2 disabled:opacity-60 transition shadow-lg
    ${isCompany
      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 shadow-blue-500/25'
      : 'bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-500 hover:to-cyan-500 shadow-teal-500/25'}`;

  return (
    <div className="min-h-screen flex bg-white">

      {/* ── LEFT: Branding ──────────────────────────────────── */}
      <div className={`hidden lg:flex w-[45%] flex-col justify-between px-16 py-12
        ${isCompany
          ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-blue-950'
          : 'bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900'}
        relative overflow-hidden`}>
        <div className="absolute inset-0 pointer-events-none"
          style={{
            background: isCompany
              ? 'radial-gradient(ellipse at 25% 65%, rgba(37,99,235,0.2) 0%, transparent 60%)'
              : 'radial-gradient(ellipse at 20% 60%, rgba(20,184,166,0.18) 0%, transparent 65%)'
          }} />

        <div className="relative">
          <img src="/logo-white.png" alt="ShramSena" className="h-11 w-auto" />
        </div>

        <div className="relative space-y-6">
          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center
            ${isCompany ? 'bg-blue-500/20 border border-blue-500/30' : 'bg-teal-500/20 border border-teal-500/30'}`}>
            <ShieldCheck className={`h-8 w-8 ${isCompany ? 'text-blue-400' : 'text-teal-400'}`} />
          </div>
          <div>
            <p className={`text-xs font-bold uppercase tracking-[0.2em] mb-3
              ${isCompany ? 'text-blue-400' : 'text-teal-400'}`}>
              Account Recovery
            </p>
            <h2 className="text-4xl font-bold text-white leading-snug">
              Reset your<br />
              <span className={isCompany ? 'text-blue-400' : 'text-teal-400'}>
                password securely.
              </span>
            </h2>
          </div>
          <p className="text-slate-400 text-base leading-relaxed max-w-sm">
            We'll verify your identity via OTP on your registered phone number before
            allowing a password reset.
          </p>
          <div className="space-y-3 pt-2">
            {['OTP sent to your registered phone', 'Expires in 5 minutes', 'Secure & encrypted'].map((t) => (
              <div key={t} className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0
                  ${isCompany ? 'bg-blue-500/20 border border-blue-500/40' : 'bg-teal-500/20 border border-teal-500/40'}`}>
                  <div className={`w-2 h-2 rounded-full ${isCompany ? 'bg-blue-400' : 'bg-teal-400'}`} />
                </div>
                <span className="text-slate-300 text-sm">{t}</span>
              </div>
            ))}
          </div>
        </div>

        <p className="relative text-slate-600 text-xs">
          © {new Date().getFullYear()} ShramSena™ — Your Trusted Healthcare Staffing Partner
        </p>
      </div>

      {/* ── DIVIDER ─────────────────────────────────────────── */}
      <div className="hidden lg:block w-px bg-gradient-to-b from-transparent via-gray-200 to-transparent" />

      {/* ── RIGHT: Form ─────────────────────────────────────── */}
      <div className="flex-1 flex flex-col justify-center px-8 sm:px-12 lg:px-16 py-12">
        <div className="w-full max-w-sm mx-auto">

          <div className="lg:hidden mb-8">
            <img src="/logo-horizontal.png" alt="ShramSena" className="h-9 w-auto" />
          </div>

          <button onClick={() => step === 1 ? navigate(loginPath) : setStep(1)}
            className="flex items-center gap-1.5 text-gray-400 hover:text-gray-600
              text-sm mb-10 transition group">
            <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
            {step === 1 ? 'Back to Login' : 'Change phone number'}
          </button>

          {/* Step indicator */}
          <div className="flex items-center gap-3 mb-8">
            {[1, 2].map((s) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold
                  transition-all
                  ${step >= s
                    ? (isCompany ? 'bg-blue-600 text-white' : 'bg-teal-600 text-white')
                    : 'bg-gray-100 text-gray-400'}`}>
                  {s}
                </div>
                <span className={`text-xs font-medium ${step >= s ? 'text-slate-700' : 'text-gray-400'}`}>
                  {s === 1 ? 'Verify Phone' : 'New Password'}
                </span>
                {s < 2 && <div className="w-8 h-px bg-gray-200 ml-1" />}
              </div>
            ))}
          </div>

          {/* ── STEP 1: Phone ── */}
          {step === 1 && (
            <>
              <div className="mb-8">
                <p className={`text-xs font-bold uppercase tracking-[0.15em] mb-2
                  ${isCompany ? 'text-blue-600' : 'text-teal-600'}`}>
                  Forgot Password
                </p>
                <h1 className="text-3xl font-bold text-slate-900 mb-1">Verify your phone</h1>
                <p className="text-gray-400 text-sm">
                  Enter your registered phone number to receive a reset OTP
                </p>
              </div>
              <form onSubmit={handleSendOtp} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Registered Phone Number
                  </label>
                  <input value={phone} onChange={(e) => setPhone(e.target.value)}
                    placeholder="9876543210" maxLength={10} required
                    className={inputClass} />
                </div>
                <button type="submit" disabled={loading} className={btnClass}>
                  {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending OTP...</> : 'Send OTP'}
                </button>
              </form>
            </>
          )}

          {/* ── STEP 2: OTP + New Password ── */}
          {step === 2 && (
            <>
              <div className="mb-8">
                <p className={`text-xs font-bold uppercase tracking-[0.15em] mb-2
                  ${isCompany ? 'text-blue-600' : 'text-teal-600'}`}>
                  Reset Password
                </p>
                <h1 className="text-3xl font-bold text-slate-900 mb-1">Set new password</h1>
                <p className="text-gray-400 text-sm">
                  OTP sent to <span className="font-semibold text-slate-600">{phone}</span>
                </p>
                {devOtp && (
                  <div className="mt-2 inline-flex items-center gap-2 bg-amber-50 border border-amber-200
                    rounded-lg px-3 py-1.5">
                    <span className="text-xs text-amber-700 font-medium">Dev OTP:</span>
                    <span className="text-xs font-bold text-amber-800 tracking-widest">{devOtp}</span>
                  </div>
                )}
              </div>
              <form onSubmit={handleReset} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    6-Digit OTP
                  </label>
                  <input value={otp} onChange={(e) => setOtp(e.target.value)}
                    placeholder="123456" maxLength={6} required
                    className={inputClass} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    New Password
                  </label>
                  <input type="password" value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Min 8 characters" required
                    className={inputClass} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Confirm New Password
                  </label>
                  <input type="password" value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter password" required
                    className={inputClass} />
                </div>
                <button type="submit" disabled={loading} className={btnClass}>
                  {loading
                    ? <><Loader2 className="h-4 w-4 animate-spin" /> Resetting...</>
                    : 'Reset Password'}
                </button>
              </form>
            </>
          )}

          <div className="mt-8 pt-6 border-t border-gray-100 text-sm text-gray-400">
            <p>
              Remembered your password?{' '}
              <Link to={loginPath}
                className={`font-semibold hover:underline ${isCompany ? 'text-blue-600' : 'text-teal-600'}`}>
                Sign in →
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
